"use client";

import { SectionTitle } from "@/components/shared/SectionTitle";
import { PixelButton } from "@/components/shared/PixelButton";
import { RarityBadge } from "@/components/shared/RarityBadge";
import { AnimatedFeatureCard } from "@/components/how-it-works/FeatureCard";
import { ParameterTable } from "@/components/how-it-works/ParameterTable";
import { type Rarity } from "@/types/cat";
import { motion } from "framer-motion";
import { Coins, Flame, Combine, TrendingUp, Vault, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

const mintRows = [
  ["Total Supply", <span key="s" className="font-mono">1,024</span>],
  ["Mint Price", <span key="p" className="font-mono">0.0025 ETH</span>],
  ["Max per Wallet", <span key="l" className="font-mono">5 cats</span>],
  ["Target Chain", <span key="c">Robinhood Chain</span>],
];

const burnRows = [
  ["Asleep (Dormant)", "0", "0×"],
  ["Active", "25,000", "1×"],
  ["Climb I", "75,000", "1.4×"],
  ["Climb II", "150,000", "1.9×"],
  ["Climb III", "300,000", "2.5×"],
  ["Alpha Burn (Max)", "850,000", "3.5×"],
];

const fuseRows = [
  [
    "Fuse 2 cats",
    <span key="f2" className="font-mono">50,000</span>,
    <span key="w2" className="font-mono">(w1 + w2) × 120 / 100</span>,
  ],
  [
    "Fuse 3 cats",
    <span key="f3" className="font-mono">100,000</span>,
    <span key="w3" className="font-mono">(w1 + w2 + w3) × 130 / 100</span>,
  ],
];

const revenueRows = [
  [
    "Active Holder Reward Pool",
    <span key="r80" className="font-mono text-success">80%</span>,
    "Auto-swapped to NVDA + SPCX + SPY every hour",
  ],
  [
    "Operations & Keeper Gas",
    <span key="r20" className="font-mono">20%</span>,
    "Team, frontend, and keeper bot maintenance",
  ],
];

const rarities: { rarity: Rarity; ratio: string; weight: number; multiplier: string }[] = [
  { rarity: "common", ratio: "70%", weight: 100, multiplier: "1.00×" },
  { rarity: "rare", ratio: "25%", weight: 115, multiplier: "1.15×" },
  { rarity: "legendary", ratio: "4%", weight: 135, multiplier: "1.35×" },
  { rarity: "mythic", ratio: "1%", weight: 160, multiplier: "1.60×" },
];

export default function HowItWorksPage() {
  return (
    <div className="container mx-auto px-4 py-10 md:py-16">
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="relative mb-12 overflow-hidden rounded-3xl border border-border"
      >
        <img
          src="/brand/banner.png"
          alt="Cat Claw banner"
          className="h-48 w-full object-cover md:h-72"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-background/90 via-background/60 to-transparent" />
        <div className="absolute inset-0 flex items-center px-6 md:px-12">
          <div>
            <p className="font-pixel text-xs text-primary">HOW IT WORKS</p>
            <h1 className="mt-2 font-pixel text-xl text-foreground md:text-3xl">
              Start Your <span className="text-primary">Claw</span> Journey
            </h1>
          </div>
        </div>
      </motion.div>

      <SectionTitle
        subtitle=""
        title="How It Works"
        description="Mint pixel cats, burn $CATCLAW to upgrade multipliers, fuse stronger cats, and split platform fees by multiplier to earn NVDA + SPCX + SPY stock tokens."
        centered
      />

      <div className="mt-12 grid gap-6">
        {/* Mint */}
        <AnimatedFeatureCard
          index={0}
          icon={Coins}
          title="Mint"
          step="01"
        >
          <p className="mb-4">
            Pay <strong className="text-foreground">0.0025 ETH</strong>{" "}
            to mint a pixel cat of random rarity. Every cat has a unique base, accessories, background, and rarity.
          </p>
          <ParameterTable headers={["Parameter", "Value"]} rows={mintRows} />
          <p className="mt-4 text-sm">
            Mint fees flow into the platform fee pool.{" "}
            <strong className="text-foreground">80%</strong>{" "}
            is returned to active holders as stock tokens, and{" "}
            <strong className="text-foreground">20%</strong> covers operations and keeper gas.
          </p>
        </AnimatedFeatureCard>

        {/* Burn & Fuse side by side */}
        <div className="grid gap-6 md:grid-cols-2">
          <AnimatedFeatureCard
            index={1}
            icon={Flame}
            title="Burn & Upgrade"
            step="02"
          >
            <p className="mb-4">
              Newly minted cats start dormant and cannot earn rewards. Burn{" "}
              <strong className="text-foreground">$CATCLAW</strong>{" "}
              to activate your cat and increase its multiplier. Burned tokens are permanently destroyed.
            </p>
            <ParameterTable
              headers={["Stage", "Total Burned", "Tier Multiplier"]}
              rows={burnRows.map((row) =>
                row.map((cell, index) => (
                  <span key={index} className="font-mono">
                    {cell}
                  </span>
                ))
              )}
            />
            <div className="mt-4 rounded-lg border border-border bg-background p-4">
              <p className="font-pixel text-xs text-primary">Formula</p>
              <p className="mt-1 font-mono text-sm text-foreground">
                finalWeight = rarityWeight × tierWeight / 100
              </p>
            </div>
            <p className="mt-4 text-sm">
              After a cat is sold on the secondary market, it enters dormancy. The new holder must burn{" "}
              <strong className="text-foreground">25,000 $CATCLAW</strong>{" "}
              to reactivate it.
            </p>
          </AnimatedFeatureCard>

          <AnimatedFeatureCard
            index={2}
            icon={Combine}
            title="Fuse"
            step="03"
          >
            <p className="mb-4">
              Fuse 2 or 3 cats into one stronger &quot;survivor&quot;. The fused cats are burned, and their vault assets and weights are merged into the survivor.
            </p>
            <ParameterTable
              headers={["Operation", "Extra Burn", "Weight Calculation"]}
              rows={fuseRows}
            />
            <ul className="mt-4 list-disc space-y-1.5 pl-4 text-sm">
              <li>Fused NFTs are destroyed directly</li>
              <li>Vault balances are merged into the survivor cat</li>
              <li>Fusing does not change the image or metadata</li>
            </ul>
          </AnimatedFeatureCard>
        </div>

        {/* Earn & Vault side by side */}
        <div className="grid gap-6 md:grid-cols-2">
          <AnimatedFeatureCard
            index={3}
            icon={TrendingUp}
            title="Earn"
            step="04"
          >
            <p className="mb-4">
              Platform fees are distributed to holders based on each cat&apos;s final weight.{" "}
              <strong className="text-foreground">80%</strong>{" "}
              goes into the reward pool, auto-settled and swapped to stock tokens every hour.
            </p>
            <ParameterTable
              headers={["Destination", "Ratio", "Description"]}
              rows={revenueRows}
            />
            <p className="mt-4 text-sm">
              Reward assets are{" "}
              <strong className="text-foreground">NVDA + SPCX + SPY</strong>,{" "}
              three Robinhood Stock Tokens.
            </p>
          </AnimatedFeatureCard>

          <AnimatedFeatureCard
            index={4}
            icon={Vault}
            title="Vault"
            step="05"
          >
            <p className="mb-4">
              Every cat has a built-in vault. Rewards are not sent directly to your wallet; they are recorded by tokenId and stored inside the cat&apos;s vault.
            </p>
            <ul className="list-disc space-y-2 pl-4 text-sm">
              <li>
                <strong className="text-foreground">Moves with the cat:</strong>{" "}
                when you sell the cat, the stock tokens in its vault go to the buyer
              </li>
              <li>
                <strong className="text-foreground">Only holder can withdraw:</strong>{" "}
                only the current ownerOf(tokenId) can withdraw
              </li>
              <li>
                <strong className="text-foreground">Multi-asset withdrawal:</strong>{" "}
                withdraw all three stock tokens at once
              </li>
            </ul>
          </AnimatedFeatureCard>
        </div>

        {/* Rarity & weights */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-50px" }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="rounded-xl border border-border bg-card p-6 md:p-8"
        >
          <h3 className="mb-4 text-lg font-bold">Rarity Base Multipliers</h3>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {rarities.map((item) => (
              <div
                key={item.rarity}
                className="flex items-center justify-between rounded-lg border border-border bg-background p-4"
              >
                <div className="flex items-center gap-2">
                  <RarityBadge rarity={item.rarity} />
                  <span className="text-xs text-muted-foreground">{item.ratio}</span>
                </div>
                <div className="text-right">
                  <p className="font-mono text-sm font-semibold text-foreground">
                    {item.multiplier}
                  </p>
                  <p className="text-xs text-muted-foreground">Weight {item.weight}</p>
                </div>
              </div>
            ))}
          </div>
          <p className="mt-4 text-sm text-muted-foreground">
            Example: a Rare cat upgraded to Alpha Burn has final weight = 115 × 350 / 100 ={" "}
            <strong className="text-foreground">403</strong>; a Mythic at the same tier ={" "}
            <strong className="text-foreground">560</strong>.
          </p>
        </motion.div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-50px" }}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="mt-8 rounded-3xl border border-border bg-card p-8 text-center md:p-12"
        >
          <h2 className="font-pixel text-xl md:text-3xl">
            Ready to enter the <span className="text-primary">Stonk</span> market?
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-muted-foreground">
            Minting opens soon. Browse the gallery first to see all 1,024 unique pixel cats.
          </p>
          <div className="mt-8 flex flex-col items-center justify-center gap-4 sm:flex-row">
            <Link
              to="/collection"
              className="inline-flex h-14 items-center gap-2 border-2 border-primary px-8 font-pixel text-sm text-primary transition-colors hover:bg-primary/10 pixel-corners"
            >
              View Gallery
              <ArrowRight size={18} />
            </Link>
            <PixelButton size="lg">Mint Soon</PixelButton>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
